import { NgModule, Optional, SkipSelf } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AccountManagementComponent } from './account-management/account-management.component';
import { AuthGuard } from './auth.guard';

import { SharedClinicalDataComponent } from './clinic-studies/shared-clinical-data/shared-clinical-data.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { LoginPageComponent } from './login-page/login-page.component';
import { CommunicationLogComponent } from './mail/communication-log/communication-log.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { ProfileAuthGuard } from './profile-auth.guard';
import { RecoverPasswordComponent } from './recovery/recover-password/recover-password.component';
import { RegistrationPageComponent } from './registration-page/registration-page.component';
import { RepoBusinessComponent } from './repo-business/repo-business/repo-business.component';
import { SaleCompanyAdminRoleGuard } from './sale-company-admin-role.gaurd';
import { SaleCompanyRoleGuard } from './sale-company-role.guard';
import { SaleGrantedAllComponent } from './sales/sale-granted-all/sale-granted-all.component';
import { SaleGrantedComponent } from './sales/sale-granted/sale-granted.component';
import { SaleListComponent } from './sales/sale-list/sale-list.component';
import { SaleSettingComponent } from './sales/sale-setting/sale-setting.component';
import { NotificationTypeEditComponent } from './settings/notification-type/notification-type-edit/notification-type-edit.component';
import { NotificationTypeListComponent } from './settings/notification-type/notification-type-list/notification-type-list.component';
import { SettingsComponent } from './settings/settings/settings.component';
import { VetSettingsComponent } from './settings/vet-settings/vet-settings.component';
import { VetUserNotificationSettingComponent } from './settings/vet-user-notification-setting/vet-user-notification-setting.component';
import { SuperUserRoleGuard } from './super-user-role.guard';
import { TermsAndConditionsComponent } from './terms-and-conditions/terms-and-conditions.component';
import { UserDetailComponent } from './user-management/user-detail/user-detail.component';
import { UserListComponent } from './user-management/user-list/user-list.component';
import { VetSaleCompanyRoleGuard } from './vet-and-sale-company-role.guard';

const routes: Routes = [
  {
    path: '',
    component: LoginPageComponent,
  },
  {
    path: 'granted',
    component: SaleGrantedComponent,
  },
  {
    path: 'grantedall',
    component: SaleGrantedAllComponent,
  },
  {
    path: 'register',
    component: RegistrationPageComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'recovery/:id',
    component: RecoverPasswordComponent,
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./profile-page/profile-page.module').then(
        (m) => m.ProfilePageModule
      ),
    canActivate: [ProfileAuthGuard],
  },
  {
    path: 'sales',
    component: SaleListComponent,
    canActivate: [AuthGuard, VetSaleCompanyRoleGuard],
  },
  {
    path: 'sales/:id',
    loadChildren: () =>
      import('./sales/sale-index.module').then((m) => m.SalesModule),
    canActivate: [AuthGuard, VetSaleCompanyRoleGuard],
  },
  {
    path: 'sale-submissions',
    loadChildren: () =>
      import('./submissions/submissions-index.module').then(
        (m) => m.SubmissionsModule
      ),
    canActivate: [AuthGuard, VetSaleCompanyRoleGuard],
  },
  {
    path: 'sales/:id/sale-settings',
    component: SaleSettingComponent,
    //pathMatch: 'full',
    canActivate: [AuthGuard, SaleCompanyRoleGuard],
  },
  {
    path: 'account-management',
    component: AccountManagementComponent,
    canActivate: [AuthGuard, SaleCompanyRoleGuard],
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [AuthGuard, SuperUserRoleGuard],
  },
  {
    path: 'notification-type',
    component: NotificationTypeListComponent,
    canActivate: [AuthGuard, SuperUserRoleGuard],
  },
  {
    path: 'notification-type-modify/:id',
    component: NotificationTypeEditComponent,
    canActivate: [AuthGuard, SuperUserRoleGuard],
  },
  {
    path: 'company-settings',
    component: SettingsComponent,
    canActivate: [AuthGuard, SaleCompanyRoleGuard],
  },
  {
    path: 'vet-settings',
    component: VetSettingsComponent,
    canActivate: [AuthGuard, VetSaleCompanyRoleGuard],
  },
  {
    path: 'vet-notification-settings',
    component: VetUserNotificationSettingComponent,
    canActivate: [AuthGuard, VetSaleCompanyRoleGuard],
  },
  {
    path: 'users',
    component: UserListComponent,
    canActivate: [AuthGuard, SaleCompanyAdminRoleGuard],
  },
  {
    path: 'user-detail/:id',
    component: UserDetailComponent,
    canActivate: [AuthGuard, SaleCompanyAdminRoleGuard],
  },
  {
    path: 'user-detail',
    component: UserDetailComponent,
    canActivate: [AuthGuard, SaleCompanyAdminRoleGuard],
  },
  {
    path: 'contacts',
    component: RepoBusinessComponent,
    canActivate: [AuthGuard, VetSaleCompanyRoleGuard],
  },
  {
    path: 'shareStudy',
    component: SharedClinicalDataComponent,
  },
  {
    path: 'clinic-studies',
    loadChildren: () =>
      import('./clinic-studies/clinic-studies.module').then(
        (m) => m.ClinicStudiesModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'organizations',
    loadChildren: () =>
      import('./repo-business/repo-business.module').then(
        (m) => m.RepoBusinessModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'statistics',
    loadChildren: () =>
      import('./statistics/statistics.module').then((m) => m.StatisticsModule),
    //canActivate: [AuthGuard, SaleCompanyRoleGuard],
  },
  {
    path: 'communication-log',
    component: CommunicationLogComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'terms-conditions',
    component: TermsAndConditionsComponent,
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
  },
  // {
  //   path: 'viewer-alt',
  //   component: OhifIframedViewerComponent,
  //   canActivate: [AuthGuard],
  // },
  // {
  //   path: 'xray-series-summary',
  //   component: XraySeriesSummaryComponent,
  //   //canActivate: [UserTrack]
  // },

  // Fallback when no prior routes is matched
  { path: '**', redirectTo: '', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
  public constructor(
    @Optional()
    @SkipSelf()
    parentModule: AppRoutingModule
  ) {
    if (parentModule) {
      throw new Error(
        'AppRoutingModule is already loaded. Import it in the AppModule only.'
      );
    }
  }
}
